import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {AuthService} from './auth.service';
import {Carrier} from '../interfaces/anagraphic.interfaces';
import {Warehouse} from '../interfaces/Warehouses.interfaces';

/**
 * Risposta di POST /api/pallets/returns/import-csv. Gli elenchi restano non
 * tipizzati: il contratto dei singoli elementi non è fissato dal backend, che
 * li costruisce dal parsing del CSV.
 */
export interface ReturnsCsvImportResponse {
    status: string;
    receivedCharacters: number;
    /** Righe interpretate correttamente nel file */
    parsedRows: number;
    /** Resi effettivamente chiusi a database */
    appliedRows: number;
    /** Righe valide ma non applicate (reso inesistente, già consegnato, già al quinto stato) */
    skippedRows: number;
    /** Righe malformate */
    errorRows: number;
    applied: any[];
    skipped: any[];
    errors: any[];
    /** ISO string dall'OffsetDateTime del backend */
    receivedAt: string;
}

/**
 * Body di POST /api/pallets/close: i tre codici individuano il bancale, la data
 * di consegna va inviata come OffsetDateTime ISO (es. "2026-09-07T10:30:00+02:00").
 */
export interface PalletCloseRequest {
    palletCode: string;
    palletWaybillCode: string;
    palletShipmentCode: string;
    palletActualDeliveryDate: string;
    /**
     * Magazzino con cui registrare il quinto stato: uno di quelli restituiti da
     * /close-warehouses. Se assente il backend usa il magazzino storico.
     */
    palletWarehouseCode?: string;
}

/** I tre codici che individuano un bancale lato backend */
export interface PalletCloseItem {
    palletCode: string;
    palletWaybillCode: string;
    palletShipmentCode: string;
}

/**
 * Body di POST /api/pallets/close-bulk: una data di consegna comune, un
 * magazzino di chiusura comune e l'elenco dei bancali da chiudere.
 */
export interface PalletCloseBulkRequest {
    palletActualDeliveryDate: string;
    /** Magazzino comune a tutti i bancali; come in PalletCloseRequest */
    palletWarehouseCode?: string;
    pallets: PalletCloseItem[];
}

/**
 * Risposta di POST /api/pallets/close-warehouses: i magazzini selezionabili e
 * quello da preselezionare (la destinazione comune dei bancali, altrimenti
 * WHS00001; null se nessuno dei due è tra i selezionabili).
 */
export interface PalletCloseWarehousesResponse {
    warehouses: Warehouse[];
    defaultWarehouseCode: string | null;
}

/**
 * Risposta di POST /api/pallets/close-bulk: stessa forma dell'import CSV
 * (applied / skipped / errors con i relativi conteggi).
 */
export interface PalletCloseBulkResponse {
    status: string;
    receivedItems: number;
    /** Bancali effettivamente chiusi */
    appliedRows: number;
    /** Bancali non trovati o già al quinto stato */
    skippedRows: number;
    /** Elementi senza i tre codici */
    errorRows: number;
    applied: any[];
    skipped: any[];
    errors: any[];
    /** ISO string dall'OffsetDateTime del backend */
    receivedAt: string;
}

/**
 * Chiusura dei resi da file: invio del contenuto CSV e lettura dell'esito
 * dell'importazione (PalletController, /api/pallets).
 */
@Injectable({
    providedIn: 'root'
})
export class ReturnsService {
    private apiUrl = `${environment.apiUrl}/pallets`;
    private token = this.authService.getAccessToken();
    private language = localStorage.getItem('language');
    /** L'endpoint accetta il CSV come testo puro: Content-Type text/plain, non JSON */
    private httpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'text/plain',
            'Authorization': `Bearer ${this.token}`,
            'Accept-language': this.language ?? 'it'
        })
    };
    /** Header JSON per gli altri endpoint del controller (l'import CSV usa text/plain) */
    private jsonHttpOptions = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${this.token}`,
            'Accept-language': this.language ?? 'it'
        })
    };

    constructor(private http: HttpClient, private authService: AuthService) {
    }

    /**
     * Invia il contenuto CSV dei resi da chiudere: il backend valorizza la data
     * di consegna e crea il quinto stato per ogni riga applicabile. Le righe
     * non valide o non applicabili non fanno fallire la chiamata, tornano in
     * `errors` e `skipped`, così il file si può correggere e ricaricare senza
     * duplicare le chiusure già fatte.
     * @param csvContent Righe "codice reso;codice bolla;codice dealer;data"
     */
    importReturnsCsv(csvContent: string): Observable<ReturnsCsvImportResponse> {
        return this.http.post<ReturnsCsvImportResponse>(
            `${this.apiUrl}/returns/import-csv`, csvContent, this.httpOptions)
            .pipe(
                catchError(this.handleError)
            );
    }

    /**
     * Stampa delle bolle di reso: il backend costruisce un unico PDF con tutte
     * le bolle richieste (i pallet che condividono lo stesso PALLET_WAYBILL_CODE).
     * Backend: POST /api/pallets/waybills-print (PalletController)
     * Body: array dei codici bolla -> application/pdf (404 se nessun documento)
     */
    printWaybills(palletWaybillCodes: string[]): Observable<Blob> {
        const codes = (palletWaybillCodes || [])
            .map((code) => (code || '').trim())
            .filter((code) => !!code);
        return this.http.post(`${this.apiUrl}/waybills-print`, codes, {
            headers: this.jsonHttpOptions.headers,
            responseType: 'blob'
        }).pipe(
            catchError((error) => {
                console.error('Errore HTTP stampa bolle di reso:', error);
                // Errore originale rilanciato: il chiamante distingue lo status (es. 404)
                return throwError(() => error);
            })
        );
    }

    /**
     * Chiusura manuale di un singolo bancale: il backend ne valorizza la data
     * di consegna e registra il quinto stato. Se il bancale ha già il quinto
     * stato la chiamata fallisce e nulla viene scritto.
     * Backend: POST /api/pallets/close (PalletController)
     * @param request codici del bancale e data di consegna (ISO con offset)
     * @return il bancale aggiornato (Opr_Pallets_DTO)
     */
    closePallet(request: PalletCloseRequest): Observable<any> {
        return this.http.post<any>(`${this.apiUrl}/close`, request, this.jsonHttpOptions)
            .pipe(
                catchError((error) => {
                    console.error('Errore HTTP chiusura manuale bancale:', error);
                    // Errore originale rilanciato: il popup mostra il messaggio del backend
                    return throwError(() => error);
                })
            );
    }

    /**
     * Chiusura manuale di più bancali con la stessa data di consegna. Un
     * bancale non trovato o già al quinto stato finisce in `skipped`, un
     * elemento senza i tre codici in `errors`, senza fermare gli altri; un
     * errore di scrittura annulla invece l'intera chiamata.
     * Backend: POST /api/pallets/close-bulk (PalletController)
     */
    closePalletsBulk(request: PalletCloseBulkRequest): Observable<PalletCloseBulkResponse> {
        return this.http.post<PalletCloseBulkResponse>(`${this.apiUrl}/close-bulk`, request, this.jsonHttpOptions)
            .pipe(
                catchError((error) => {
                    console.error('Errore HTTP chiusura multipla bancali:', error);
                    // Errore originale rilanciato: il popup mostra il messaggio del backend
                    return throwError(() => error);
                })
            );
    }

    /**
     * Magazzini tra cui scegliere quello di chiusura per i bancali indicati:
     * quelli autorizzati all'utente più la destinazione di ciascun bancale,
     * ordinati per nome, con il magazzino da preselezionare. Con elenco vuoto
     * restano i soli magazzini autorizzati.
     * Backend: POST /api/pallets/close-warehouses (PalletController)
     */
    getCloseWarehouses(pallets: PalletCloseItem[]): Observable<PalletCloseWarehousesResponse> {
        return this.http.post<PalletCloseWarehousesResponse>(`${this.apiUrl}/close-warehouses`, pallets, this.jsonHttpOptions)
            .pipe(
                catchError((error) => {
                    console.error('Errore HTTP caricamento magazzini di chiusura:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Elenco dei vettori per la tendina del popup "Resi per vettore"
     * (campo carrierCode, etichetta carrierName).
     * Backend: GET /api/anagraphics/carriers
     */
    getCarriers(): Observable<Carrier[]> {
        return this.http.get<Carrier[]>(`${environment.apiUrl}/anagraphics/carriers`, this.jsonHttpOptions)
            .pipe(
                catchError((error) => {
                    console.error('Errore HTTP caricamento vettori:', error);
                    return throwError(() => error);
                })
            );
    }

    /**
     * Genera l'Excel "Resi per vettore": foglio di riepilogo e un foglio per
     * ogni vettore con i resi nell'intervallo di date (yyyy-MM-dd, sulla data stato).
     * Backend: GET /api/pallets/returns-report (PalletController)
     * Query: dateFrom, dateTo, carrierCodes (ripetuto per ogni codice) -> file xlsx.
     * carrierCodes è obbligatorio come parametro ma può essere vuoto: senza
     * codici viene inviato `carrierCodes=` e il backend include tutti i vettori
     * (se manca del tutto risponde 400).
     */
    generateReturnsReport(dateFrom: string, dateTo: string, carrierCodes: string[]): Observable<Blob> {
        let params = new HttpParams()
            .set('dateFrom', dateFrom)
            .set('dateTo', dateTo);
        const codes = (carrierCodes || []).filter((code) => !!code);
        if (codes.length === 0) {
            params = params.set('carrierCodes', '');
        }
        codes.forEach((code) => {
            params = params.append('carrierCodes', code);
        });
        return this.http.get(`${this.apiUrl}/returns-report`, {
            headers: this.jsonHttpOptions.headers,
            params,
            responseType: 'blob'
        }).pipe(
            catchError((error) => {
                console.error('Errore HTTP generazione report resi per vettore:', error);
                // Errore originale rilanciato: il popup mostra il messaggio del backend
                return throwError(() => error);
            })
        );
    }

    // =============================================
    // ERROR HANDLING
    // =============================================

    /**
     * Gestione centralizzata degli errori
     */
    private handleError(error: any): Observable<never> {
        let errorMessage = 'Si è verificato un errore sconosciuto';
        if (error.error instanceof ErrorEvent) {
            // Errore lato client
            errorMessage = `Errore: ${error.error.message}`;
        } else {
            // Errore lato server
            switch (error.status) {
                case 400:
                    errorMessage = 'Il file inviato non è valido o supera la dimensione consentita';
                    break;
                case 401:
                    errorMessage = 'Non autorizzato';
                    break;
                case 403:
                    errorMessage = 'Non hai i permessi per chiudere i resi';
                    break;
                case 404:
                    errorMessage = 'Risorsa non trovata';
                    break;
                case 415:
                    errorMessage = 'Formato del contenuto non supportato';
                    break;
                case 422:
                    errorMessage = error.error?.message || 'Dati non validi';
                    break;
                case 500:
                    errorMessage = 'Errore interno del server';
                    break;
                default:
                    errorMessage = `Errore ${error.status}: ${error.error?.message || error.statusText}`;
            }
        }

        console.error('Errore HTTP import-csv resi:', error);
        return throwError(() => new Error(errorMessage));
    }
}
